import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";

export const LanguageSwitcher = ({ variant = "desktop" }) => {
  const { lang, setLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find((l) => l.code === lang) || languages[0];

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (variant === "mobile") {
    return (
      <div className="flex flex-wrap gap-3" data-testid="language-switcher-mobile">
        {languages.map((l) => (
          <button
            key={l.code}
            data-testid={`lang-${l.code}`}
            onClick={() => setLang(l.code)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
              l.code === lang ? "border-evo-gold text-evo-gold" : "border-white/15 text-evo-pearl/70 hover:text-evo-pearl"
            }`}
          >
            {l.short}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="language-switcher-btn"
        data-cursor="view"
        className="flex items-center gap-2 text-evo-pearl/80 hover:text-evo-gold transition-colors duration-300 text-sm"
        aria-label="Select language"
      >
        <Globe size={16} />
        <span className="tracking-wide">{current.short}</span>
        <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 rtl:right-auto rtl:left-0 mt-4 w-44 bg-evo-black/95 backdrop-blur-xl border border-white/10 py-2 z-50"
          >
            {languages.map((l) => (
              <li key={l.code}>
                <button
                  data-testid={`lang-${l.code}`}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors duration-200 ${
                    l.code === lang ? "text-evo-gold" : "text-evo-pearl/80 hover:text-evo-pearl hover:bg-white/5"
                  }`}
                >
                  <span>{l.label}</span>
                  {l.code === lang && <Check size={14} />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
